import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import { ApplicationApi } from './application-api';
import { runServiceDeskWorkflow, ServiceDeskWorkflow } from './application-workflows';
import { ApplicationBase, ListParams } from './service-desk.types';

interface ApplicationHooksConfig<TItem extends ApplicationBase> {
  /** Query-key segment, unique per sub-module (`noc`, `byod`, ...). */
  key: string;
  api: ApplicationApi<TItem>;
  /** The workflow that withdraws an application (it checks the applicant and the Pending status). */
  withdrawWorkflow: ServiceDeskWorkflow;
}

/**
 * TanStack Query hooks for one application schema. Every write is a service desk workflow (see each
 * sub-module's `*.service.ts`), which enforces the business rules; this only wires them to the cache
 * and to the toasts, which are identical across the five sub-modules.
 */
export const createApplicationHooks = <TItem extends ApplicationBase>({
  key,
  api,
  withdrawWorkflow,
}: ApplicationHooksConfig<TItem>) => {
  const rootKey = ['hr-service-desk', key] as const;

  const useApplications = (params: ListParams, enabled = true) =>
    useGlobalQuery({
      queryKey: [...rootKey, 'list', params],
      queryFn: () => api.list(params),
      enabled,
      staleTime: 30 * 1000,
    });

  const useApplication = (itemId: string | undefined) =>
    useGlobalQuery({
      queryKey: [...rootKey, 'item', itemId],
      queryFn: () => api.getById(itemId as string),
      enabled: Boolean(itemId),
    });

  /**
   * Runs `mutationFn`, then refreshes every list of this schema, the all-types My Requests table
   * (`['service-requests', …]`) and Home's open requests, and shows `successKey`.
   */
  const useApplicationMutation = <TVariables, TResult = unknown>(
    mutationFn: (variables: TVariables) => Promise<TResult>,
    successKey: string
  ) => {
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const { t } = useTranslation();

    return useGlobalMutation({
      mutationFn,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: rootKey });
        queryClient.invalidateQueries({ queryKey: ['service-requests'] });
        queryClient.invalidateQueries({ queryKey: ['home', 'open-service-requests'] });
        toast({ title: t(successKey) });
      },
    });
  };

  const useWithdrawApplication = () =>
    useApplicationMutation(
      (itemId: string) => runServiceDeskWorkflow(withdrawWorkflow, { itemId }),
      'REQUEST_WITHDRAWN'
    );

  return { useApplications, useApplication, useApplicationMutation, useWithdrawApplication };
};
